/**
 * Stateful math.js functions for the computed-tags engine — `ema`,
 * `rolling_mean`, `lag`, `stddev_n`. These are flagged "Preview · Phase 1"
 * in the editor because their semantics must be validated against the
 * server-side window-buffer worker before customers depend on them.
 *
 * Each function maintains a per-call-site ring buffer of recent input
 * values. The "call site" key is computed from the evaluation context
 * the engine sets before each tick: `{computedTagId}:{callIndex}`. So if
 * the same formula calls `ema(x, 60)` twice, each gets its own buffer.
 *
 * Buffers live at module scope so they persist across the React render
 * cycle. They auto-evict the oldest sample when full.
 */

import type { MathJsInstance } from "mathjs";

interface EvalContext {
  /** Stable id for the formula being evaluated (computed-tag id or "preview"). */
  ownerId: string;
  /** Counter incremented on every stateful-fn call; gives each call site a unique key. */
  callIndex: number;
}

const NO_CONTEXT: EvalContext = { ownerId: "_orphan", callIndex: 0 };
let currentContext: EvalContext = NO_CONTEXT;

/**
 * Set the evaluation context before calling `compiled.evaluate(scope)`.
 * The compute-tag store + the preview hook wrap each evaluate() call
 * in setEvalContext(...) so stateful funcs index their buffers correctly.
 */
export function setEvalContext(ownerId: string) {
  currentContext = { ownerId, callIndex: 0 };
}

export function clearEvalContext() {
  currentContext = NO_CONTEXT;
}

/**
 * Bounded ring buffer keyed by `${ownerId}:${callIndex}`. The keys never
 * collide across computed tags because they're scoped by ownerId.
 */
const buffers = new Map<string, number[]>();
const MAX_BUFFER_SIZE = 4096;

function bufferKey(): string {
  const key = `${currentContext.ownerId}:${currentContext.callIndex}`;
  currentContext.callIndex += 1;
  return key;
}

function pushSample(key: string, value: number, cap: number) {
  let buf = buffers.get(key);
  if (!buf) {
    buf = [];
    buffers.set(key, buf);
  }
  buf.push(value);
  const limit = Math.min(Math.max(1, cap), MAX_BUFFER_SIZE);
  if (buf.length > limit) buf.splice(0, buf.length - limit);
}

/** Reset every buffer — used when the user resets the demo or edits the formula. */
export function clearStatefulBuffers(ownerId?: string) {
  if (!ownerId) {
    buffers.clear();
    return;
  }
  for (const key of [...buffers.keys()]) {
    if (key.startsWith(`${ownerId}:`)) buffers.delete(key);
  }
}

/**
 * Exponential moving average. period_samples ≥ 1 — typical EMA constant
 * α = 2 / (N + 1). Bootstraps on the first sample.
 *
 * Usage: `ema(T_raw, 60)`
 */
function ema(value: number, periodSamples: number): number {
  const key = bufferKey();
  if (!Number.isFinite(value)) return NaN;
  const n = Math.max(1, Math.floor(periodSamples));
  const alpha = 2 / (n + 1);
  const prev = buffers.get(key);
  const last = prev && prev.length > 0 ? prev[prev.length - 1] : value;
  const next = alpha * value + (1 - alpha) * last;
  pushSample(key, next, n + 1);
  return next;
}

/**
 * Simple moving average over the last N samples. Less responsive than
 * EMA but more visually intuitive for users.
 *
 * Usage: `rolling_mean(T_raw, 30)`
 */
function rolling_mean(value: number, n: number): number {
  const key = bufferKey();
  if (!Number.isFinite(value)) return NaN;
  pushSample(key, value, Math.max(1, Math.floor(n)));
  const buf = buffers.get(key)!;
  let sum = 0;
  for (const v of buf) sum += v;
  return sum / buf.length;
}

/**
 * Returns the value N samples ago (or the oldest available, if fewer
 * samples have arrived). Useful for lag features in time-series models.
 *
 * Usage: `lag(T_raw, 5)`
 */
function lag(value: number, n: number): number {
  const key = bufferKey();
  if (!Number.isFinite(value)) return NaN;
  const lookback = Math.max(1, Math.floor(n));
  pushSample(key, value, lookback + 1);
  const buf = buffers.get(key)!;
  // Oldest if buffer not yet full; lagged otherwise.
  const idx = Math.max(0, buf.length - 1 - lookback);
  return buf[idx];
}

/**
 * Sample standard deviation over the last N samples. Returns 0 with a
 * single sample (math.js convention; user code should treat the early
 * warmup window as "not yet meaningful").
 *
 * Usage: `stddev_n(T_raw, 30)`
 */
function stddev_n(value: number, n: number): number {
  const key = bufferKey();
  if (!Number.isFinite(value)) return NaN;
  pushSample(key, value, Math.max(2, Math.floor(n)));
  const buf = buffers.get(key)!;
  if (buf.length < 2) return 0;
  const mean = buf.reduce((s, v) => s + v, 0) / buf.length;
  const variance =
    buf.reduce((s, v) => s + (v - mean) ** 2, 0) / (buf.length - 1);
  return Math.sqrt(variance);
}

/**
 * Register the four stateful functions with the math.js instance.
 * Idempotent via `override: true` — re-import in dev / HMR doesn't
 * double-register. Called from engine.ts after the math instance is
 * constructed.
 */
let registered: WeakSet<object> = new WeakSet();
export function registerStatefulFunctions(math: MathJsInstance) {
  if (registered.has(math)) return;
  math.import(
    {
      ema,
      rolling_mean,
      lag,
      stddev_n,
    },
    { override: true },
  );
  registered.add(math);
}
